/*
 * "New message" (`POST /api/chat/dm`): pick one person, or several, and land in the conversation
 * with them. The route is get-or-create — the same people always resolve to the same conversation —
 * so the dialog never has to ask whether one already exists, and picking the same person twice
 * cannot leave two half-empty threads behind.
 *
 * The picker is the people area's, the same control the groups screens use: who may be chosen is
 * one rule, and it lives with the people rather than with each surface that offers them.
 */

import { useState } from "react"
import { PeoplePicker } from "../../../core/groups/index.ts"
import { t as coreT } from "../../../core/i18n/index.ts"
import type { UserId } from "../../../core/store/model.ts"
import { useModel } from "../../../core/store/store.ts"
import { useAsyncAction } from "../../../lib/useAsyncAction.ts"
import { Button, Dialog, FormAlert } from "../../../ui/index.ts"
import { openDirectMessage } from "../conversation/api.ts"
import { goTo, whenConversationArrives } from "../conversation/opening.ts"
import { t } from "../i18n/t.ts"
import { dmPath } from "../navigation.ts"
import "./sidebar.css"

export function NewDirectMessageDialog({
	open,
	onOpenChange,
}: {
	open: boolean
	onOpenChange: (open: boolean) => void
}) {
	const meId = useModel((model) => model.me?.id ?? 0)
	const [selected, setSelected] = useState<UserId[]>([])

	const start = useAsyncAction(async () => {
		const opened = await openDirectMessage(selected)
		await whenConversationArrives(opened.conversation.id)
		onOpenChange(false)
		reset()
		goTo(dmPath(opened.conversation.dmHandle ?? ""))
	})

	function reset() {
		setSelected([])
		start.reset()
	}

	return (
		<Dialog
			open={open}
			onOpenChange={(next) => {
				onOpenChange(next)
				if (!next) reset()
			}}
			title={t("chat.newDm.title")}
			description={t("chat.newDm.body")}
			footer={
				<div className="tb-dialog__actions">
					<Button
						variant="neutral"
						label={coreT("action.cancel")}
						onClick={() => onOpenChange(false)}
					/>
					<Button
						variant="primary"
						label={t("chat.newDm.start")}
						disabled={selected.length === 0 || start.busy}
						onClick={() => void start.run()}
					/>
				</div>
			}
		>
			{/* The picker prints its own "n selected" under its list, so this dialog prints none: two
			    of them under one list is the same sentence said twice (X-B1 D6). */}
			<PeoplePicker
				exclude={[meId]}
				selected={selected}
				onChange={setSelected}
				legend={t("chat.newDm.pick")}
			/>
			<FormAlert messageKey={start.error} />
		</Dialog>
	)
}
